import { FilterValueDTO, ProteinFormulationDTO } from 'business/models/Models';
import CrystalService from 'business/services/CrystalService';
import { downloadFile } from 'utils/Global';
import { IStyle } from 'Styles';
import { IChip } from 'components/Chip';
import { IWellDropsData } from 'redux/hooks/searchnFilter';
import { getRandomNegativeNumber } from 'components/main/Setup/Imaging/utils';

export interface ISearchResultsRowMap {
  plateId?: number;
  batchId?: number;
  wellId?: number;
  wellNumber?: number;
  dropNumber?: number;
  wellDropId?: number;
  regionID?: number;
  captureProfileId?: number;
  proteinFormulationId?: number;
  regionName?: string;
}

const crystalService = new CrystalService();

export const handleAttachmentDownload = async (fileId: number) => {
  const file = await crystalService.downloadCrystalAttachment(fileId);
  downloadFile(file.data, file.fileName);
};

export const fieldsMap = {
  plateId: 'Experiment Plate.ID',
  batchId: 'Capture Result.Image Batch ID',
  wellId: 'Well.ID',
  wellNumber: 'Well.Number',
  dropNumber: 'Well Drop.Number',
  wellDropId: 'Well Drop.ID',
  regionID: 'Region.ID',
  captureProfileId: 'Capture Result.Profile ID',
  proteinFormulationId: 'Protein Formulation.ID',
} as const;

export interface IChipWithId extends IChip {
  id: string;
}

export const styles: IStyle = {
  modify: {
    background: '#4CAF50',
    width: '105px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    gap: '8px',
    color: 'white',
  },
  reset: {
    background: '#313131',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
  },
  disabled: {
    background: '#CACACA',
    color: '#9e9e9e !Important',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
  },
  applyEnabled: {
    background: '#4CAF50',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
  },
};

export const getProteinInfo = (
  pfInfo: ProteinFormulationDTO,
  dropsData: IWellDropsData,
) => {
  if (!pfInfo) {
    return {};
  }
  return {
    dropNumber: dropsData.dropNumber,
    dropProteinVolume: dropsData.dropProteinVolume,
    dropWellVolume: dropsData.wellVolume,
    name: pfInfo.pfName,
    proteinLotId: pfInfo.proteinLotId,
    volume: pfInfo.volume,
    datePrepared: pfInfo.datePrepared,
    storageTemp: pfInfo.storageTemp as unknown as number,
    pclass: pfInfo.class,
    extCoefficient: pfInfo.extCoefficient,
    liquidClassName: pfInfo.class,
  };
};

export const getFilterValueDTO = (filterId: number, text: string): FilterValueDTO => {
  return new FilterValueDTO({
    id: getRandomNegativeNumber(),
    filterId,
    value: text,
  });
};

export const formatDateDisplay = (value: string) => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const milliseconds = date.getMilliseconds().toString().padStart(3, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const getMinMaxDates = (dateArr: string[]) => {
  const dates = dateArr
    .filter((date) => date !== null)
    .map((dateTime) => new Date(dateTime.split(' ')[0]));
  const timestamps = dates.map((date) => date.getTime());
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const minDate = new Date(minTimestamp);
  const maxDate = new Date(maxTimestamp);
  const minYear = minDate.getFullYear();
  const minMonth = (minDate.getMonth() + 1).toString().padStart(2, '0');
  const minDay = minDate.getDate().toString().padStart(2, '0');
  const maxYear = maxDate.getFullYear();
  const maxMonth = (maxDate.getMonth() + 1).toString().padStart(2, '0');
  const maxDay = maxDate.getDate().toString().padStart(2, '0');

  const minDateFormatted = `${minYear}-${minMonth}-${minDay}`;
  const maxDateFormatted = `${maxYear}-${maxMonth}-${maxDay}`;
  return [minDateFormatted, maxDateFormatted];
};

export const getFilteredFiltersByValuePredicate = (value: string, searchTerm: string) => {
  return (
    (('none'.includes(searchTerm.toLowerCase()) || searchTerm === '') && value == null) ||
    value?.toLowerCase().includes(searchTerm?.toLowerCase())
  );
};
